/**
 * Утилита для определения платформы и режима запуска
 */

export type PlatformMode = 'telegram' | 'pwa-standalone' | 'browser-safari' | 'browser-other';

/**
 * Определяет режим запуска приложения
 */
export function detectPlatformMode(): PlatformMode {
  // 1. Проверяем Telegram WebApp
  if (window.Telegram?.WebApp?.initData) {
    return 'telegram';
  }
  
  // 2. Проверяем PWA standalone mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as any).standalone === true // iOS Safari
    || document.referrer.includes('android-app://'); // Android TWA
  
  if (isStandalone) {
    return 'pwa-standalone';
  }
  
  // 3. Проверяем Safari
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (isSafari) {
    return 'browser-safari';
  }
  
  // 4. Другие браузеры
  return 'browser-other';
}

/**
 * Проверяет, работает ли приложение в Telegram
 */
export function isTelegramApp(): boolean {
  return !!window.Telegram?.WebApp?.initData;
}

/**
 * Проверяет, работает ли приложение в PWA режиме
 */
export function isPWAMode(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as any).standalone === true
    || document.referrer.includes('android-app://');
}

/**
 * Проверяет, работает ли приложение в Safari
 */
export function isSafari(): boolean {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

/**
 * Получает safe area insets для текущей платформы
 */
export function getSafeAreaInsets() {
  const mode = detectPlatformMode();
  
  // Базовые значения
  const insets = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  };
  
  // Пытаемся получить значения из CSS env()
  const style = getComputedStyle(document.documentElement);
  
  // Telegram обычно сам управляет safe areas
  if (mode === 'telegram') {
    // Telegram WebApp обычно уже учитывает safe areas
    return insets;
  }
  
  // Для PWA и Safari пытаемся получить реальные значения
  const topEnv = parseInt(style.getPropertyValue('env(safe-area-inset-top)') || '0');
  const bottomEnv = parseInt(style.getPropertyValue('env(safe-area-inset-bottom)') || '0');
  const leftEnv = parseInt(style.getPropertyValue('env(safe-area-inset-left)') || '0');
  const rightEnv = parseInt(style.getPropertyValue('env(safe-area-inset-right)') || '0');
  
  return {
    top: topEnv || 0,
    bottom: bottomEnv || 0,
    left: leftEnv || 0,
    right: rightEnv || 0
  };
}

/**
 * Применяет CSS класс в зависимости от платформы
 */
export function applyPlatformClass() {
  const mode = detectPlatformMode();
  const bodyEl = document.body;
  
  // Удаляем все существующие классы платформы
  bodyEl.classList.remove('platform-telegram', 'platform-pwa', 'platform-safari', 'platform-browser');
  
  // Добавляем соответствующий класс
  switch (mode) {
    case 'telegram':
      bodyEl.classList.add('platform-telegram');
      break;
    case 'pwa-standalone':
      bodyEl.classList.add('platform-pwa');
      break;
    case 'browser-safari':
      bodyEl.classList.add('platform-safari');
      break;
    case 'browser-other':
      bodyEl.classList.add('platform-browser');
      break;
  }
  
  return mode;
}

/**
 * Инициализирует CSS переменные для адаптации под платформу
 */
export function initPlatformStyles() {
  const mode = applyPlatformClass();
  const root = document.documentElement;
  
  // Устанавливаем CSS переменные в зависимости от платформы
  switch (mode) {
    case 'telegram':
      // Telegram: минимальные отступы, так как Telegram сам управляет UI
      root.style.setProperty('--platform-safe-top', '0px');
      root.style.setProperty('--platform-safe-bottom', '0px');
      root.style.setProperty('--platform-padding-top', '25px');
      root.style.setProperty('--platform-padding-bottom', '20px');
      root.style.setProperty('--platform-padding-horizontal', '20px');
      break;
      
    case 'pwa-standalone':
      // PWA: учитываем safe areas
      root.style.setProperty('--platform-safe-top', 'env(safe-area-inset-top, 0px)');
      root.style.setProperty('--platform-safe-bottom', 'env(safe-area-inset-bottom, 0px)');
      root.style.setProperty('--platform-padding-top', 'calc(25px + env(safe-area-inset-top, 0px))');
      root.style.setProperty('--platform-padding-bottom', 'calc(20px + env(safe-area-inset-bottom, 0px))');
      root.style.setProperty('--platform-padding-horizontal', 'max(20px, env(safe-area-inset-left, 0px), env(safe-area-inset-right, 0px))');
      break;
      
    case 'browser-safari':
      // Safari: стандартные отступы + немного дополнительного пространства сверху
      root.style.setProperty('--platform-safe-top', '0px');
      root.style.setProperty('--platform-safe-bottom', '0px');
      root.style.setProperty('--platform-padding-top', '25px');
      root.style.setProperty('--platform-padding-bottom', '20px');
      root.style.setProperty('--platform-padding-horizontal', '20px');
      break;
      
    case 'browser-other':
      // Другие браузеры: стандартные отступы
      root.style.setProperty('--platform-safe-top', '0px');
      root.style.setProperty('--platform-safe-bottom', '0px');
      root.style.setProperty('--platform-padding-top', '25px');
      root.style.setProperty('--platform-padding-bottom', '20px');
      root.style.setProperty('--platform-padding-horizontal', '20px');
      break;
  }
  
  console.log(`[Platform] Detected mode: ${mode}`);
  
  // Следим за изменениями display-mode (PWA add to homescreen)
  setupDisplayModeWatcher();
  
  return mode;
}

/**
 * Отслеживает изменения display-mode и обновляет стили
 */
function setupDisplayModeWatcher() {
  // Следим за изменением display-mode
  const standaloneQuery = window.matchMedia('(display-mode: standalone)');
  
  // Колбэк при изменении
  const handleDisplayModeChange = (e: MediaQueryListEvent | MediaQueryList) => {
    if (e.matches) {
      console.log('[Platform] Display mode changed to standalone (PWA)');
      initPlatformStyles();
    }
  };
  
  // Слушаем изменения (современный API)
  if (standaloneQuery.addEventListener) {
    standaloneQuery.addEventListener('change', handleDisplayModeChange);
  } else {
    // Fallback для старых браузеров
    standaloneQuery.addListener(handleDisplayModeChange as any);
  }
  
  // Также отслеживаем изменение размера окна (может влиять на safe areas)
  let resizeTimeout: NodeJS.Timeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const mode = detectPlatformMode();
      console.log(`[Platform] Window resized, current mode: ${mode}`);
      // Переинициализируем стили при изменении размера
      initPlatformStyles();
    }, 300);
  });
}

